/**
 * iter318 wave 15 — useWebsiteContent hook.
 *
 * Fetches CMS-managed content from `/api/public/website/*`.
 * Returns `{ loading, error, content }` — components that consume it
 * MUST fall back to their static content when `content` is null or
 * the requested collection is empty.
 *
 *   const { content } = useWebsiteContent('home');
 *   const sections = content?.sections?.length ? content.sections : [];
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cached per page (memory + localStorage) so remounts and repeat visits paint styled content instantly.
const LS_KEY = 'vyomos_cms_v1:';
const memo = {};
const readCache = (page) => {
  if (memo[page]) return memo[page];
  try { const raw = localStorage.getItem(LS_KEY + page); if (raw) memo[page] = JSON.parse(raw); } catch (e) { /* ignore */ }
  return memo[page] || null;
};
const writeCache = (page, data) => {
  memo[page] = data;
  try { localStorage.setItem(LS_KEY + page, JSON.stringify(data)); } catch (e) { /* quota */ }
};

export function useWebsiteContent(page = 'home') {
  const [content, setContent] = useState(() => readCache(page));
  const [loading, setLoading] = useState(() => !readCache(page));
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const cached = readCache(page);
    setContent(cached);
    setLoading(!cached);
    axios.get(`${BACKEND_URL}/api/public/website/content`, { params: { page } })
      .then((r) => { writeCache(page, r.data); if (alive) { setContent(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page]);

  return { content, loading, error };
}

export function useWebsitePricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/public/website/pricing`)
      .then((r) => { if (alive) { setPricing(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return { pricing, loading, error };
}
